import React, { useState } from 'react'
import classnames from 'classnames'

import Icon from '@components/Icon'
import Button from '@components/Button'
import Textfield from '@components/Textfield'

import { vehiclesSelector } from 'dashboard-account/selectors'

import { getPlateRegex } from 'shared/utils'

import styles from './styles.module.scss'

const Tab = ({ children, onRemove, onClick }) => (
    <div onClick={onClick} className={classnames('py-2', 'px-3', 'mr-3', styles.tab)}>
        <span className="mr-2">{children}</span>
        {onRemove && (
            <span onClick={onRemove}>
                <Icon className={styles.close} type="close" size="xxs" />
            </span>
        )}
    </div>
)

const VehicleTabs = ({ userVehicles, vehicles = [], setSelectedVehicles, translate: t, disabled }) => {
    const [newPlateInput, setNewPlateInput] = useState(null)
    const [showPlateError, setShowPlateError] = useState(false)
    const [addPlateOpen, setAddPlateOpen] = useState(false)
    const [showVehiclesList, setShowVehiclesList] = useState(false)

    const setUserVehicle = vehicle => {
        setSelectedVehicles([...vehicles, { id: vehicle.id, tag: vehicle.tag }])
    }

    const removeVehicle = vehicle => {
        setSelectedVehicles(vehicles.filter(v => v.tag !== vehicle.tag))
    }

    const showAddPlate = () => {
        setAddPlateOpen(true)

        if (showVehiclesList) {
            setShowVehiclesList(false)
        }
    }

    const saveNewTag = () => {
        if (newPlateInput) {
            setUserVehicle({ tag: newPlateInput })
            setNewPlateInput(null)
            setAddPlateOpen(false)
        }
    }

    const closeTagInput = () => {
        setAddPlateOpen(false)
        setShowPlateError(false)

        if (newPlateInput) {
            setNewPlateInput(null)
        }
    }

    return (
        <>
            <span className={classnames('mt-4', 'mb-3', styles.columnLabel)}>
                <T>dashboard.subscriptions.assign.parker.label.vehicles</T>
            </span>
            <div className={styles.vehicleTabs}>
                {vehicles.map((veh, i) => (
                    <Tab onRemove={!disabled && (() => removeVehicle(veh))} key={i}>
                        {veh.tag}
                    </Tab>
                ))}
            </div>
            {!addPlateOpen && (
                <div className="mt-3 d-md-flex">
                    <Button
                        btnStyle="outline"
                        type="button"
                        className={classnames('mr-3', styles.addPlateBtn)}
                        onClick={showAddPlate}
                        disabled={disabled}>
                        <T>dashboard.subscriptions.assign.parker.label.add.plate</T>
                    </Button>
                    {showVehiclesList ? (
                        <Button
                            type="button"
                            onClick={() => setShowVehiclesList(false)}
                            btnStyle="link"
                            className="mt-3 mt-md-0">
                            <T>dashboard.subscriptions.assign.parker.label.vehicles.list.close</T>
                        </Button>
                    ) : (
                        <Button
                            type="button"
                            btnStyle="link"
                            onClick={() => setShowVehiclesList(true)}
                            className="mt-sm-3 mt-md-0"
                            disabled={disabled}>
                            <T>dashboard.subscriptions.assign.parker.label.vehicles.list</T>
                        </Button>
                    )}
                </div>
            )}
            {showVehiclesList && (
                <div className={classnames('mt-3', styles.userVehicleTabs)}>
                    {userVehicles
                        .filter(v => !vehicles.includes(v.tag))
                        .map((v, i) => (
                            <Tab onClick={() => setUserVehicle(v)} key={i}>
                                {v.tag}
                            </Tab>
                        ))}
                </div>
            )}
            {addPlateOpen && (
                <>
                    <div className={classnames('d-flex', 'align-items-center', 'mt-3')}>
                        <Textfield
                            className={styles.addVehicleInput}
                            label={t('dashboard.subscriptions.assign.parker.placeholder.add.plate')}
                            onChange={e => setNewPlateInput(e?.target?.value)}
                            required
                            regex={getPlateRegex()}
                            enterSymbolsValidation
                            setShowError={setShowPlateError}
                            status={showPlateError ? 'error' : undefined}
                        />
                        <Button type="button" className="ml-2" btnStyle="primary" onClick={saveNewTag}>
                            <T>dashboard.account.save</T>
                        </Button>
                        <Button type="button" btnStyle="link" onClick={closeTagInput}>
                            <T>dashboard.label.cancel</T>
                        </Button>
                    </div>
                    {showPlateError && (
                        <span className="text-small text-red">{t('dashboard.label.licence.plate.invalid')}</span>
                    )}
                </>
            )}
        </>
    )
}

export default compose(
    withTranslate,
    connect(
        createStructuredSelector({
            userVehicles: vehiclesSelector,
        })
    )
)(VehicleTabs)
